import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { ApiConfigurationsComponent } from 'src/app/components/api-configurations/api-configurations.component';
import { DesignerComponent } from 'src/app/components/designer/designer.component';
import { FolderTrackingCustomizationComponent } from 'src/app/components/folder-tracking-customization/folder-tracking-customization.component';
import { SmsTemplatesComponent } from 'src/app/components/sms-templates/sms-templates.component';
import { SubscriptionsComponent } from 'src/app/components/subscriptions/subscriptions.component';
import { ApplicationsComponent } from '../app/components/applications/applications.component';
import { ArticlesComponent } from '../app/components/articles/articles.component';
import { AttributesComponent } from '../app/components/attributes/attributes.component';
import { BoxGroupsComponent } from '../app/components/box-groups/box-groups.component';
import { BoxesComponent } from '../app/components/boxes/boxes.component';
import { ChannelComponent } from '../app/components/channel/channel.component';
import { ContactsComponent } from '../app/components/contacts/contacts.component';
import { CustomersComponent } from '../app/components/customers/customers.component';
import { DocumentTypesComponent } from '../app/components/document-types/document-types.component';
import { EmailAddressesComponent } from '../app/components/email-addresses/email-addresses.component';
import { EmailsTemplatesComponent } from '../app/components/emails-templates/emails-templates.component';
import { FolderStatesComponent } from '../app/components/folder-states/folder-states.component';
import { GroupsComponent } from '../app/components/groups/groups.component';
import { HomeComponent } from '../app/components/home/home.component';
import { ListLayoutsBoxesComponent } from '../app/components/list-layouts-boxes/list-layouts-boxes.component';
import { ListLayoutsWorkflowsComponent } from '../app/components/list-layouts-workflows/list-layouts-workflows.component';
import { LogSupervisionComponent } from '../app/components/log-supervision/log-supervision.component';
import { LoginComponent } from '../app/components/login/login.component';
import { MailConnectorErrorSupervisionComponent } from '../app/components/mail-connector-error-supervision/mail-connector-error-supervision.component';
import { OrganizationParametersComponent } from '../app/components/organization-parameters/organization-parameters.component';
import { OrganizationsComponent } from '../app/components/organizations/organizations.component';
import { ParametersComponent } from '../app/components/parameters/parameters.component';
import { ProcessDefinitionsComponent } from '../app/components/process-definitions/process-definitions.component';
import { RolesComponent } from '../app/components/roles/roles.component';
import { UsersComponent } from '../app/components/users/users.component';
import { ViewsComponent } from '../app/components/views/views.component';
import { RouteData } from '../app/core/route-data';
import { Rights } from '../app/models/enums/rights';
import { OrganizationSelectGuard } from '../app/organization-select.guard';
import { UserConnectedGuard } from '../app/user-connected.guard';
import { UserGrantedGuard } from '../app/user-granted.guard';
import { ViewSelectGuard } from '../app/view-select.guard';

const routes: Routes = [
    { path: '', redirectTo: 'home', pathMatch: 'full' },
    { path: 'home', component: HomeComponent, canActivate: [UserConnectedGuard, OrganizationSelectGuard] },
    {
        path: 'groups',
        component: GroupsComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, UserGrantedGuard],
        data: new RouteData([Rights.GroupAndUserRead]),
    },
    {
        path: 'users',
        component: UsersComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, UserGrantedGuard],
        data: new RouteData([Rights.GroupAndUserRead]),
    },
    {
        path: 'applications',
        component: ApplicationsComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, UserGrantedGuard],
        data: new RouteData([]),
    },
    {
        path: 'views',
        component: ViewsComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, UserGrantedGuard],
        data: new RouteData([Rights.ViewRead]),
    },
    {
        path: 'boxes',
        component: BoxesComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, ViewSelectGuard, UserGrantedGuard],
        data: new RouteData([Rights.BoxRead]),
    },
    {
        path: 'boxgroups',
        component: BoxGroupsComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, ViewSelectGuard, UserGrantedGuard],
        data: new RouteData([Rights.BoxRead]),
    },
    {
        path: 'roles',
        component: RolesComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, UserGrantedGuard],
        data: new RouteData([Rights.RoleRead]),
    },
    {
        path: 'customers',
        component: CustomersComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, UserGrantedGuard],
        data: new RouteData([Rights.CustomerRead]),
    },
    {
        path: 'contacts',
        component: ContactsComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, UserGrantedGuard],
        data: new RouteData([Rights.ContactRead]),
    },
    {
        path: 'documentTypes',
        component: DocumentTypesComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, UserGrantedGuard],
        data: new RouteData([Rights.DocumentTypeRead]),
    },
    {
        path: 'emailAddresses',
        component: EmailAddressesComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, UserGrantedGuard],
        data: new RouteData([Rights.EmailAddressRead]),
    },
    {
        path: 'channels',
        component: ChannelComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, ViewSelectGuard, UserGrantedGuard],
        data: new RouteData([Rights.ProcessRead]),
    },
    {
        path: 'articles',
        component: ArticlesComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, UserGrantedGuard],
        data: new RouteData([Rights.ArticleRead]),
    },
    {
        path: 'workflow-designer',
        component: DesignerComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, ViewSelectGuard, UserGrantedGuard],
        data: {
            expectedRights: [Rights.WorkflowDefinitionRead],
        },
    },
    {
        path: 'attributes',
        component: AttributesComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, ViewSelectGuard, UserGrantedGuard],
        data: new RouteData([Rights.AttributRead]),
    },
    {
        path: 'listLayoutsBoxes',
        component: ListLayoutsBoxesComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, ViewSelectGuard, UserGrantedGuard],
        data: new RouteData([Rights.ListLayoutRead]),
    },
    {
        path: 'listLayoutsWorkflows',
        component: ListLayoutsWorkflowsComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, ViewSelectGuard, UserGrantedGuard],
        data: new RouteData([Rights.ListLayoutRead]),
    },
    {
        path: 'folderStates',
        component: FolderStatesComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, UserGrantedGuard],
        data: new RouteData([Rights.FolderStateRead]),
    },
    {
        path: 'emailsTemplates',
        component: EmailsTemplatesComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, UserGrantedGuard],
        data: new RouteData([Rights.EmailTemplateRead], true),
    },
    {
        path: 'views/emailsTemplates',
        component: EmailsTemplatesComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, ViewSelectGuard, UserGrantedGuard],
        data: new RouteData([Rights.EmailTemplateRead], false),
    },
    {
        path: 'smsTemplates',
        component: SmsTemplatesComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, UserGrantedGuard],
        data: new RouteData([Rights.SmsTemplateRead], true),
    },
    {
        path: 'views/smsTemplates',
        component: SmsTemplatesComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, ViewSelectGuard, UserGrantedGuard],
        data: new RouteData([Rights.SmsTemplateRead], false),
    },
    {
        path: 'parameters',
        component: ParametersComponent,
        canActivate: [UserConnectedGuard, UserGrantedGuard],
        data: new RouteData([Rights.ParameterRead]),
    },
    {
        path: 'organization/parameters',
        component: OrganizationParametersComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, UserGrantedGuard],
        data: new RouteData([Rights.ParameterRead]),
    },
    {
        path: 'organization/subscriptions',
        component: SubscriptionsComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, UserGrantedGuard],
        data: new RouteData([Rights.SubscriptionRead]),
    },
    {
        path: 'processDefinitions',
        component: ProcessDefinitionsComponent,
        canActivate: [UserConnectedGuard, OrganizationSelectGuard, ViewSelectGuard, UserGrantedGuard],
        data: new RouteData([Rights.ProcessRead]),
    },
    {
        path: 'organizations',
        component: OrganizationsComponent,
        canActivate: [UserConnectedGuard, UserGrantedGuard],
        data: new RouteData([Rights.OrganizationRead]),
    },
    {
        path: 'log-supervision',
        component: LogSupervisionComponent,
        canActivate: [UserConnectedGuard, UserGrantedGuard],
        data: new RouteData([Rights.LogSupervision]),
    },
    {
        path: 'log-mail-connector',
        component: MailConnectorErrorSupervisionComponent,
        canActivate: [UserConnectedGuard, UserGrantedGuard],
        data: new RouteData([Rights.LogSupervision]),
    },
    {
        path: 'apiConfigurations',
        component: ApiConfigurationsComponent,
        canActivate: [UserConnectedGuard, UserGrantedGuard, OrganizationSelectGuard, ViewSelectGuard],
        data: new RouteData([Rights.ApiConfigurationRead]),
    },
    {
        path: 'folderTrackingCustomization',
        component: FolderTrackingCustomizationComponent,
        canActivate: [UserConnectedGuard, UserGrantedGuard, OrganizationSelectGuard, ViewSelectGuard],
        data: new RouteData([Rights.CustomizeFolderTracking]),
    },
    { path: 'login', component: LoginComponent },
];

@NgModule({
    imports: [RouterModule.forRoot(routes, { onSameUrlNavigation: 'ignore' })],
    exports: [RouterModule],
})
export class AppRoutingModule {}
